// Éditeur de devis : révisions et variantes, lignes par section (tableau sur ordinateur, cartes sur mobile),
// ajout depuis le catalogue / les exigences non chiffrées / ligne libre, totaux, paramètres, hypothèses et exclusions,
// flux de validation et d'émission (dérogation motivée), révision émise en lecture seule, exports.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BadgeCheck, BookOpen, Boxes, CopyPlus, FileDown, FilePlus2, FileSearch, FileSpreadsheet, HardHat, ListChecks, Lock, Pencil, Plus, Send, ShieldCheck, Stamp, Timer, Undo2 } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { dateHeure, euros, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, EtatErreur, EtatVide, IndicateurEnregistrement, LienBouton, Panneau, useConfirmation, useNotifier,
  type EtatEnregistrement,
} from '../../components/ui.tsx';
import { BadgeStatutDevis, STATUTS_DEVIS } from '../../components/statuts.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import {
  BadgeARenseigner, BadgesAlertePrix, LIBELLE_A_FIXER, LIBELLE_REMISE_GAMME, LIBELLE_SOUS_TARIF, LIBELLES_MOTIFS, LIBELLES_SECTIONS, Montant, normaliserVue, pct, PRIX_RESERVE, quantite, TOTAUX_RESERVES, SaisieAuto, SECTIONS, sourcePrix, useEtroit, versSaisie,
  type Bloc, type Ligne, type Section, type VueRevision, variationFr,
} from './commun.tsx';
import { PanneauRentabiliteDevis } from '../rentabilite/rentabilite.tsx';
import { useSession } from '../../lib/session.tsx';
import { DialogueCatalogue, DialogueDerogation, DialogueExigences, DialogueLigne, DialogueVariante } from './dialogues.tsx';
import { DialogueOuvrage } from './ouvrages.tsx';
import { BandeauAcceptation, DialogueAcceptation, DialogueAnnulationAcceptation } from './acceptation.tsx';
import { DialogueRevuePrix, nbAlertes } from './tarifs.tsx';
import { EcartsMetres } from './EcartsMetres.tsx';
import { PanneauRemiseProposee } from './remise-proposee.tsx';
import { EncadreAjustementsDevis } from '../bons/EncadreAjustementsDevis.tsx';
import { PanneauSurplusDevis } from './surplus.tsx';

interface DevisEntete { id: string; number: string; title: string; revisions: { id: string; rev_no: number; variant: string; status: string }[] }

function revisionParDefaut(revs: DevisEntete['revisions']) {
  const base = revs.filter((r) => r.variant === 'Base');
  const liste = base.length ? base : revs;
  return [...liste].sort((a, b) => b.rev_no - a.rev_no)[0]?.id;
}

export function PageEditeurDevis() {
  const { id = '', quoteId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const devis = useQuery({ queryKey: ['devis', quoteId], queryFn: () => api.get<DevisEntete>(`/api/devis/${quoteId}`) });
  const revisionId = params.get('revision') ?? (devis.data ? revisionParDefaut(devis.data.revisions) : undefined);
  const vue = useQuery({ queryKey: ['revision', revisionId], enabled: !!revisionId,
    queryFn: () => api.get<VueRevision>(`/api/devis/revisions/${revisionId}`).then(normaliserVue) });

  return (
    <DossierCadre dossierId={id} actif="devis" large
      actions={<LienBouton to={`/etudes/${id}/devis`} icone={<FileSpreadsheet aria-hidden />}>Tous les devis</LienBouton>}>
      {(devis.isLoading || vue.isLoading) && <Chargement lignes={6} />}
      {devis.isError && <EtatErreur erreur={devis.error} reessayer={() => devis.refetch()} />}
      {vue.isError && <EtatErreur erreur={vue.error} reessayer={() => vue.refetch()} />}
      {devis.data && !revisionId && <EtatVide titre="Aucune révision" texte="Ce devis ne contient aucune révision." />}
      {vue.data && <Editeur v={vue.data} dossierId={id} changerRevision={(rid) => setParams({ revision: rid })} />}
    </DossierCadre>
  );
}

function Editeur({ v, dossierId, changerRevision }: { v: VueRevision; dossierId: string; changerRevision: (rid: string) => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const etroit = useEtroit(900);
  const { confirmer, element: confirmation } = useConfirmation();
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [quand, setQuand] = useState<string | undefined>();
  const [dialogue, setDialogue] = useState<null | 'ligne' | 'catalogue' | 'ouvrage' | 'exigences' | 'derogation' | 'variante' | 'acceptation' | 'annulation' | 'revue'>(null);
  const [ligneEditee, setLigneEditee] = useState<Ligne | null>(null);
  const [flux, setFlux] = useState(false);
  const r = v.revision;
  const rid = r.id;
  const d = v.droits;
  const nav = useNavigate();
  const preparerChantier = async () => {
    setFlux(true);
    try {
      const c = await api.post<{ id: string }>(`/api/devis/revisions/${rid}/chantier`, {});
      nav(`/interventions/chantiers/${c.id}/preparer`);
    } catch (e) { notifier(messageErreur(e), true); setFlux(false); }
  };
  const modifiable = d.ecrire && r.status === 'brouillon';
  const { peut } = useSession();
  useEffect(() => { setEtat('enregistre'); setQuand(undefined); }, [rid]);
  // Variations au-delà du seuil (option tarification avancée) : « Cuivre 1/4–3/8 : +15,2 % depuis le prix du devis ».
  const variations = v.lignes.flatMap((l) => {
    const p = v.alertes_prix?.lignes[l.id]?.proposition;
    return p?.au_dela_seuil && p.variation_pct ? [{ id: l.id, label: l.label, pct: p.variation_pct }] : [];
  });

  const rafraichir = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['revision', rid] }), qc.invalidateQueries({ queryKey: ['devis', v.devis.id] }),
      qc.invalidateQueries({ queryKey: ['devis-dossier', dossierId] }), qc.invalidateQueries({ queryKey: ['exigences-non-chiffrees'] }),
      qc.invalidateQueries({ queryKey: ['dossier', dossierId] }), qc.invalidateQueries({ queryKey: ['surplus-devis', rid] }),
    ]);
  };
  /** Toute écriture passe ici : indicateur d'enregistrement, message d'erreur, rafraîchissement des totaux calculés par le serveur. */
  const ecrire = async <T,>(f: () => Promise<T>): Promise<T> => {
    setEtat('en_cours');
    try {
      const x = await f();
      await rafraichir();
      setEtat('enregistre'); setQuand(`à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`);
      return x;
    } catch (e) {
      setEtat('erreur'); notifier(messageErreur(e), true);
      throw e;
    }
  };
  const patchLigne = (lid: string, corps: Record<string, unknown>) => ecrire(() => api.patch(`/api/devis/revisions/${rid}/lignes/${lid}`, corps));
  const patchRevision = (corps: Record<string, unknown>) => ecrire(() => api.patch(`/api/devis/revisions/${rid}`, corps));
  // Ma gamme : un accord porte sur ce que le décideur VOIT. L'écran envoie les valeurs affichées ; si la ligne ou la remise a changé
  // entre-temps, le serveur refuse (409 « conflit ») : on recharge le devis pour montrer l'état à jour, le message dit quoi vérifier.
  const accordSurValeurVue = async (f: () => Promise<unknown>, ok: string) => {
    try { await ecrire(f); notifier(ok); } catch (e) {
      if (e instanceof ErreurApi && e.status === 409 && e.code === 'conflit') await rafraichir().catch(() => undefined);
    }
  };
  // Ma gamme (01/11/2026) : accord de la direction sur une baisse sous le tarif (compte gamme.achats, audité côté serveur).
  const accorder = d.accord_direction && !r.fige
    ? (l: Ligne) => accordSurValeurVue(() => api.post(`/api/devis/revisions/${rid}/lignes/${l.id}/accord-direction`,
      { prix: l.sale_unit_price, quantite: l.quantity }), 'Accord de la direction enregistré.')
    : undefined;
  // Remise globale sur une assiette contenant du matériel Ma gamme (01/11/2026) : accord de la direction (compte gamme.achats).
  const accorderRemise = d.accord_direction && !r.fige
    ? () => accordSurValeurVue(() => api.post(`/api/devis/revisions/${rid}/remise/accord-direction`,
      { pct: v.remise_gamme?.pct, empreinte: v.remise_gamme?.empreinte }), 'Remise accordée par la direction.')
    : undefined;
  /** Mise à jour EXPLICITE du prix de lignes au tarif proposé (jamais silencieuse). */
  const actualiserPrix = async (lignes: { ligneId: string; priceItemId: string }[]) => {
    const x = await ecrire(() => api.post<{ misesAJour: number }>(`/api/devis/revisions/${rid}/lignes/actualiser-prix`, { lignes }));
    notifier(`${pluriel(x.misesAJour, 'prix mis à jour', 'prix mis à jour')}.`);
    await qc.invalidateQueries({ queryKey: ['prix-reference'] });
  };
  const alertes = nbAlertes(v);
  const action = async (chemin: string, corps?: unknown, message?: string) => {
    setFlux(true);
    try { await ecrire(() => api.post(`/api/devis/revisions/${rid}/${chemin}`, corps)); if (message) notifier(message); }
    catch { /* déjà signalé */ } finally { setFlux(false); }
  };
  const nouvelleRevision = async (variante?: string) => {
    const x = await ecrire(() => api.post<{ revisionId: string; revNo: number; variante: string }>(`/api/devis/${v.devis.id}/revisions`,
      { depuisRevisionId: rid, variante: variante ?? null }));
    notifier(variante ? `Variante « ${x.variante} » créée.` : `Révision ${x.revNo} créée en brouillon.`);
    changerRevision(x.revisionId);
  };
  const emettre = async () => {
    if (!v.totaux.complet) {
      if (d.derogation) setDialogue('derogation');
      else notifier(`Émission bloquée : ${pluriel(v.totaux.nb_a_renseigner, 'ligne')} à renseigner. Seul un rôle habilité peut accorder une dérogation.`, true);
      return;
    }
    if (await confirmer('Émettre cette révision ?', 'La révision sera figée : lignes, totaux, hypothèses et exclusions ne pourront plus être modifiés. Le PDF client émis sera conservé.', 'Émettre et figer')) {
      await action('emettre', {}, 'Révision émise et figée.');
    }
  };

  const lignesTriees = useMemo(() => [...v.lignes].sort((a, b) => a.position - b.position), [v.lignes]);
  const deplacer = async (l: Ligne, sens: -1 | 1) => {
    const meme = lignesTriees.filter((x) => x.section === l.section);
    const i = meme.findIndex((x) => x.id === l.id);
    const voisin = meme[i + sens];
    if (!voisin) return;
    const ids = lignesTriees.map((x) => x.id);
    const a = ids.indexOf(l.id); const b = ids.indexOf(voisin.id);
    [ids[a], ids[b]] = [ids[b], ids[a]];
    await ecrire(() => api.post(`/api/devis/revisions/${rid}/lignes/ordre`, { ids }));
  };
  const supprimer = async (l: Ligne) => {
    if (await confirmer('Supprimer la ligne ?', `« ${l.label} » sera retirée de cette révision.`, 'Supprimer la ligne')) {
      await ecrire(() => api.del(`/api/devis/revisions/${rid}/lignes/${l.id}`));
    }
  };

  const t = v.totaux;
  // 4e passe (B2) : révision avec du Ma gamme, compte qui ne voit pas ses prix : aucun total servi, on le dit (jamais 0 ni vide).
  const reserves = !!t.totaux_reserves;
  const resumeTotal = reserves
    ? <span className="devis-resume-total"><span className="petit discret devis-totaux-reserves"><Lock size={14} aria-hidden /> {TOTAUX_RESERVES}</span></span>
    : t.complet
    ? <span className="devis-resume-total"><span className="petit secondaire">Total TTC</span> <Montant v={t.totaux!.ttc} fort /></span>
    : <span className="devis-resume-total"><BadgeARenseigner /> <span className="petit">{`Total incomplet : ${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner`}</span></span>;

  // Brouillon incomplet : la validation interne reste possible, mais l'émission au client sera bloquée (sauf dérogation motivée,
  // contrôle du serveur dans emettreRevision) ; on le dit AVANT de soumettre, près du bouton.
  const noteSoumission = r.status === 'brouillon' && d.ecrire && !t.complet
    ? `Ce devis peut être soumis à validation, mais il ne pourra être émis au client qu’une fois ${t.nb_a_renseigner > 1
      ? `les ${t.nb_a_renseigner} lignes incomplètes renseignées` : 'la ligne incomplète renseignée'}, ou avec une dérogation motivée.`
    : null;

  // Action principale du flux selon l'état
  let principal: ReactNode = null;
  const secondaires: ReactNode[] = [];
  if (r.status === 'brouillon' && d.ecrire) principal = <Bouton variante="principal" icone={<Send aria-hidden />} chargement={flux} onClick={() => action('soumettre', undefined, 'Révision soumise à validation.')}
    aria-describedby={noteSoumission ? 'devis-note-soumission' : undefined}>Soumettre à validation</Bouton>;
  if (r.status === 'a_valider' && d.valider) principal = <Bouton variante="principal" icone={<ShieldCheck aria-hidden />} chargement={flux} onClick={() => action('valider', undefined, 'Révision validée.')}>Valider</Bouton>;
  if (r.status === 'validee' && d.emettre) principal = <Bouton variante="principal" icone={<Stamp aria-hidden />} chargement={flux} onClick={emettre}>Émettre</Bouton>;
  if (r.fige && d.ecrire) principal = <Bouton variante="principal" icone={<FilePlus2 aria-hidden />} onClick={() => nouvelleRevision()}>Nouvelle révision</Bouton>;
  // Carnet de chantier : une révision acceptée (en cours) devient un chantier ; s'il existe déjà, on l'ouvre.
  if (v.chantier) {
    secondaires.push(<LienBouton key="chantier" icone={<HardHat aria-hidden />} to={`/interventions/chantiers/${v.chantier.id}`}>
      {v.chantier.reference ? `Chantier ${v.chantier.reference}` : 'Chantier en préparation'}</LienBouton>);
  } else if (r.status === 'emise' && v.acceptation && !v.acceptation.remplacee && d.preparer_chantier) {
    if (r.fige && d.ecrire) secondaires.push(<Bouton key="nouvelle-revision" icone={<FilePlus2 aria-hidden />} onClick={() => nouvelleRevision()}>Nouvelle révision</Bouton>);
    principal = <Bouton variante="principal" icone={<HardHat aria-hidden />} chargement={flux} onClick={preparerChantier}>Préparer le chantier</Bouton>;
  }
  // Estimation de pose (temps, personnes et jours, matériel, délai et dates du PDF client) : dès qu'il y a des lignes, à tout état
  // du devis ; seul le bloc « Délai et dates » s'enregistre (sur la révision en brouillon).
  if (v.lignes.length && !v.chantier) {
    secondaires.push(<LienBouton key="estimation" icone={<Timer aria-hidden />} to={`/etudes/${dossierId}/devis/${v.devis.id}/estimation?revision=${rid}`}>Estimation de pose</LienBouton>);
  }
  // Révision émise en cours, pas encore acceptée : « Marquer accepté par le client » (chargé d'affaires, administrateur).
  if (r.status === 'emise' && !v.acceptation && d.accepter) {
    secondaires.push(<Bouton key="accepter" icone={<BadgeCheck aria-hidden />} onClick={() => setDialogue('acceptation')}>Marquer accepté par le client</Bouton>);
  }
  if ((r.status === 'a_valider' || r.status === 'validee') && d.ecrire) {
    secondaires.push(<Bouton key="brouillon" icone={<Undo2 aria-hidden />} onClick={() => action('brouillon', undefined, 'Révision repassée en brouillon.')}>Repasser en brouillon</Bouton>);
  }

  return (
    <div className="pile-l devis-editeur devis-page">
      <EcartsMetres revisionId={rid} />
      {/* ── Barre du devis : révision, état, total, flux, exports ── */}
      <section className="panneau devis-barre" aria-label="Révision et actions">
        <div className="devis-barre-haut">
          <div className="pile" style={{ gap: 2, minWidth: 0 }}>
            <h2 className="devis-titre"><span className="mono">{v.devis.number}</span> {v.devis.title}</h2>
            <div className="rangee" style={{ gap: 8 }}>
              <label className="visuellement-cache" htmlFor="choix-revision">Révision affichée</label>
              <select id="choix-revision" className="saisie devis-choix-revision" value={rid} onChange={(e) => changerRevision(e.target.value)}>
                {v.revisions.map((x) => <option key={x.id} value={x.id}>{`${x.variant} — révision ${x.rev_no} (${STATUTS_DEVIS[x.status]?.libelle ?? x.status})`}</option>)}
              </select>
              <BadgeStatutDevis statut={r.status} />
              {modifiable && <IndicateurEnregistrement etat={etat} quand={quand} />}
              {/* Accès direct à la revue des prix dès le haut de l'écran (sur téléphone, le bandeau détaillé est plus bas). */}
              {(alertes.anciens > 0 || alertes.propositions > 0) && (
                <Bouton compact icone={<AlertTriangle aria-hidden />} className="devis-acces-revue" onClick={() => setDialogue('revue')}>
                  {`Prix à revoir (${Object.values(v.alertes_prix?.lignes ?? {}).filter((a) => a.ancien || a.proposition).length})`}</Bouton>
              )}
            </div>
          </div>
          {resumeTotal}
        </div>
        <div className="devis-barre-actions">
          {/* Téléphone et tablette : l'action principale passe dans la barre collante du bas (voir la fin de la page). */}
          {principal && <span className="devis-principal-haut">{principal}</span>}
          {secondaires}
          {/* 4e passe (B1) : le PDF client d'un devis Ma gamme est réservé aux comptes qui voient les prix (le serveur répond 403). */}
          {!reserves && <a className="btn" href={`/api/devis/revisions/${rid}/client.pdf`} target="_blank" rel="noopener"><FileDown aria-hidden />PDF client</a>}
          {d.couts && <a className="btn" href={`/api/devis/revisions/${rid}/interne.xlsx`}><FileSpreadsheet aria-hidden />Tableau interne</a>}
          {d.ecrire && <Bouton icone={<CopyPlus aria-hidden />} onClick={() => setDialogue('variante')}>Créer une variante</Bouton>}
        </div>
        {noteSoumission && <p className="petit devis-note-soumission" id="devis-note-soumission">{noteSoumission}</p>}
      </section>

      {r.fige && (
        <Alerte ton="info" titre={r.emitted_at ? `Version émise le ${dateHeure(r.emitted_at)} — figée` : 'Version figée'}>
          {r.status === 'remplacee' ? 'Cette révision a été remplacée par une révision plus récente. ' : ''}
          {r.emitted_by_name ? `Émise par ${r.emitted_by_name}. ` : ''}Lignes, totaux, hypothèses et exclusions sont conservés tels qu’émis.
          {d.ecrire && ' Pour modifier le devis, créez une nouvelle révision.'}
        </Alerte>
      )}
      {v.acceptation && <BandeauAcceptation a={v.acceptation} peutAnnuler={!!d.accepter} annuler={() => setDialogue('annulation')} />}
      {/* Ajouts / déductions constatés sur les bons d'intervention signés liés à ce devis (bureau seulement, option Bons d'intervention). */}
      <EncadreAjustementsDevis quoteId={v.devis.id} accepte={!!v.acceptation} />
      {/* Remise proposée (dirigeant seul) : devis brouillon avec au moins une ligne Ma gamme, ou dont la remise porte déjà sur Ma gamme. */}
      {d.remise_proposee && r.status === 'brouillon' && (v.lignes.some((l) => l.gamme_produit_id) || r.discount_scope === 'ma_gamme') && (
        <PanneauRemiseProposee revisionId={rid} apres={rafraichir} />
      )}
      {v.remise_gamme?.requis && !r.fige && (
        v.remise_gamme.accord
          ? (
            <Alerte ton="info" titre={`Remise de ${versSaisie(v.remise_gamme.accord.pct)} % accordée par la direction`}>
              {`Accordée${v.remise_gamme.accord.par ? ` par ${v.remise_gamme.accord.par}` : ''} le ${dateHeure(v.remise_gamme.accord.le)}. Tout changement du taux ou de l’assiette annule cet accord.`}
            </Alerte>
          )
          : (
            <Alerte ton="ambre" titre={LIBELLE_REMISE_GAMME}>
              {`Remise de ${versSaisie(v.remise_gamme.pct)} % sur ${v.remise_gamme.scope === 'ma_gamme' ? 'le matériel Ma gamme' : 'tout le devis'}${v.remise_gamme.saisie_par ? `, posée par ${v.remise_gamme.saisie_par}` : ''}. `}
              {accorderRemise ? 'Votre accord est nécessaire pour émettre le devis.' : 'Le devis ne pourra être émis qu’après l’accord de la direction.'}
              {/* Bouton SOUS le texte (pas à côté) : sur téléphone, le titre garde toute la largeur. */}
              {accorderRemise && (
                <div style={{ marginTop: 8 }}>
                  <Bouton icone={<ShieldCheck aria-hidden />} onClick={() => { void accorderRemise().catch(() => undefined); }}>Accorder la remise</Bouton>
                </div>
              )}
            </Alerte>
          )
      )}
      {v.derogation && (
        <Alerte ton="ambre" titre="Émis avec dérogation">
          <span>« {v.derogation.motif} » — accordée par {v.derogation.auteur} le {dateHeure(v.derogation.date)}.</span>
          <span> Lignes concernées : {v.derogation.lignes.map((l) => l.label).join(', ')}.</span>
        </Alerte>
      )}
      {(r.status === 'a_valider' || r.status === 'validee') && (
        <Alerte ton="info" titre={r.status === 'a_valider' ? 'En attente de validation' : `Validée${r.validated_by_name ? ` par ${r.validated_by_name}` : ''}${r.validated_at ? ` le ${dateHeure(r.validated_at)}` : ''}`}>
          La révision n’est plus modifiable. {d.ecrire ? 'Repassez-la en brouillon pour la corriger.' : ''}
        </Alerte>
      )}
      {(alertes.anciens > 0 || alertes.propositions > 0) && v.alertes_prix && (
        <Alerte ton="ambre" titre="Prix à revoir"
          action={<Bouton compact onClick={() => setDialogue('revue')}>Revoir les prix</Bouton>}>
          {[alertes.anciens ? `${pluriel(alertes.anciens, 'prix d’achat', 'prix d’achat')} de plus de ${v.alertes_prix.seuil_mois} mois` : null,
            alertes.propositions ? `${pluriel(alertes.propositions, 'tarif plus récent disponible', 'tarifs plus récents disponibles')}` : null].filter(Boolean).join(' ; ')}.
          {' '}Rien n’est mis à jour sans votre accord.
          {variations.length > 0 && (
            <ul className="devis-liste-simple" aria-label="Variations au-delà du seuil">
              {variations.map((x) => <li key={x.id}><strong>{x.label}</strong>{` : ${variationFr(x.pct)} depuis le prix du devis`}</li>)}
            </ul>
          )}
          {variations.length > 0 && v.alertes_prix.seuil_variation_pct && <span className="tres-petit">{`Variation signalée au-delà de ${versSaisie(v.alertes_prix.seuil_variation_pct)} %.`}</span>}
        </Alerte>
      )}
      {v.conseil_validite?.depasse && !r.fige && (
        <Alerte ton="info" titre="Articles à prix volatil">
          {`Ce devis contient des articles à prix volatil (${v.conseil_validite.articles.slice(0, 4).join(', ')}${v.conseil_validite.articles.length > 4 ? '…' : ''}) : validité conseillée ${v.conseil_validite.jours} jours (actuellement ${v.conseil_validite.validite_actuelle} jours). Rien n’est modifié sans vous.`}
        </Alerte>
      )}
      {!t.complet && (
        <Alerte ton="ambre" titre={`Total incomplet : ${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner`}>
          <ul className="devis-liste-simple">
            {t.lignes_a_renseigner.map((l) => <li key={l.id}><strong>{l.label}</strong> — {l.motifs.map((m) => LIBELLES_MOTIFS[m] ?? m).join(' ; ')}</li>)}
          </ul>
          {r.status === 'validee' && !r.fige && <span>L’émission sera bloquée tant que ces lignes ne sont pas renseignées, sauf dérogation motivée.</span>}
        </Alerte>
      )}

      {/* ── Postes ── */}
      <Panneau titre={`Postes (${v.lignes.length})`} serre actions={modifiable && (
        <div className="groupe-btn">
          {/* Proposition depuis le CCTP, le DPGF et les métrés : écran de revue dédié (rien n'est ajouté sans validation). */}
          <LienBouton compact icone={<FileSearch aria-hidden />} to={`/etudes/${dossierId}/devis/${v.devis.id}/proposition?revision=${rid}`}>Chiffrage proposé (CCTP, DPGF)</LienBouton>
          {d.couts && <Bouton compact icone={<Boxes aria-hidden />} onClick={() => setDialogue('ouvrage')}>Ouvrage type</Bouton>}
          {d.couts && <Bouton compact icone={<BookOpen aria-hidden />} onClick={() => setDialogue('catalogue')}>Depuis le catalogue</Bouton>}
          <Bouton compact icone={<ListChecks aria-hidden />} onClick={() => setDialogue('exigences')}>Depuis les exigences</Bouton>
          <Bouton compact icone={<Plus aria-hidden />} onClick={() => { setLigneEditee(null); setDialogue('ligne'); }}>Ligne libre</Bouton>
        </div>
      )}>
        {!v.lignes.length && (
          <EtatVide titre="Aucune ligne" texte={modifiable
            ? 'Ajoutez les postes depuis le chiffrage proposé (CCTP, DPGF, métrés), un ouvrage type, le catalogue de prix, les exigences non chiffrées du dossier, ou en ligne libre.'
            : 'Cette révision ne contient aucune ligne.'} />
        )}
        {!!v.lignes.length && (etroit
          ? <CartesLignes v={v} lignes={lignesTriees} modifiable={modifiable} accorder={accorder} editer={(l) => { setLigneEditee(l); setDialogue('ligne'); }} />
          : <TableauLignes v={v} lignes={lignesTriees} modifiable={modifiable} patch={patchLigne} accorder={accorder}
              editer={(l) => { setLigneEditee(l); setDialogue('ligne'); }} />)}
      </Panneau>

      {/* ── Surplus de matériel : encadré et case « Intégrer le surplus au devis » (décision du 22/10/2026) ── */}
      <PanneauSurplusDevis rid={rid} apresEcriture={rafraichir} />

      {/* ── Totaux et paramètres ── */}
      <div className="devis-bas">
        <Panneau titre="Totaux"><PanneauTotaux v={v} /></Panneau>
        {peut('rentabilite.lire') && <PanneauRentabiliteDevis revisionId={rid} />}
        <Panneau titre="Paramètres de calcul"><PanneauParametres v={v} modifiable={modifiable} patch={patchRevision} /></Panneau>
      </div>
      <Panneau titre="Hypothèses et exclusions">
        <div className="grille-2">
          <TexteAuto libelle="Hypothèses" valeur={r.hypotheses ?? ''} modifiable={modifiable} enregistrer={(x) => patchRevision({ hypotheses: x })}
            vide="Aucune hypothèse." aide="Conditions retenues pour chiffrer (accès, horaires, existant réputé conforme…). Figurent sur le PDF client." />
          <TexteAuto libelle="Exclusions" valeur={r.exclusions ?? ''} modifiable={modifiable} enregistrer={(x) => patchRevision({ exclusions: x })}
            vide="Aucune exclusion." aide="Prestations explicitement hors de l’offre. Figurent sur le PDF client." />
        </div>
      </Panneau>

      {/* ── Barre collante (≤ 900 px) : total et action principale toujours visibles, au-dessus des 5 onglets du bas ── */}
      {principal && (
        <div className="barre-actions barre-collante devis-barre-collante" role="region" aria-label="Action principale du devis">
          <span className="petit chiffre barre-collante-resume">{reserves
            ? <span className="discret devis-totaux-reserves"><Lock size={12} aria-hidden /> {TOTAUX_RESERVES}</span>
            : t.complet
            ? <><span className="secondaire">Total TTC</span> <Montant v={t.totaux!.ttc} fort /></>
            : <span className="devis-motif">{`${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner${noteSoumission ? ' avant l’émission au client' : ''}`}</span>}</span>
          {principal}
        </div>
      )}

      {/* ── Dialogues ── */}
      <DialogueLigne ouvert={dialogue === 'ligne'} fermer={() => setDialogue(null)} vue={v} ligne={ligneEditee} actualiser={actualiserPrix}
        enregistrer={(corps) => ligneEditee ? patchLigne(ligneEditee.id, corps) : ecrire(() => api.post(`/api/devis/revisions/${rid}/lignes`, corps))}
        supprimer={ligneEditee ? () => supprimer(ligneEditee) : undefined}
        deplacer={ligneEditee ? (sens) => deplacer(ligneEditee, sens) : undefined} />
      <DialogueCatalogue ouvert={dialogue === 'catalogue'} fermer={() => setDialogue(null)}
        ajouter={(corps) => ecrire(() => api.post(`/api/devis/revisions/${rid}/lignes/depuis-catalogue`, corps))}
        ajouterGamme={(corps) => ecrire(() => api.post(`/api/devis/revisions/${rid}/lignes/depuis-gamme`, corps))} />
      <DialogueOuvrage ouvert={dialogue === 'ouvrage'} fermer={() => setDialogue(null)} vue={v}
        inserer={async (corps) => {
          const x = await ecrire(() => api.post<{ creees: number; heures: string | null }>(`/api/devis/revisions/${rid}/lignes/depuis-ouvrage`, corps));
          notifier(`${pluriel(x.creees, 'ligne ajoutée', 'lignes ajoutées')}${x.heures !== null ? ` — ${quantite(x.heures)} h de base` : ' — temps de pose à renseigner'}.`);
        }} />
      <DialogueExigences ouvert={dialogue === 'exigences'} fermer={() => setDialogue(null)} dossierId={dossierId} revisionId={rid}
        ajouter={async (ids) => {
          const x = await ecrire(() => api.post<{ creees: number; ignorees: number }>(`/api/devis/revisions/${rid}/lignes/depuis-exigences`, { requirementIds: ids }));
          notifier(`${pluriel(x.creees, 'ligne ajoutée', 'lignes ajoutées')}${x.ignorees ? `, ${x.ignorees} déjà présente(s)` : ''}.`);
          return x;
        }} />
      <DialogueDerogation ouvert={dialogue === 'derogation'} fermer={() => setDialogue(null)} lignes={t.lignes_a_renseigner}
        emettre={async (motif) => { await ecrire(() => api.post(`/api/devis/revisions/${rid}/emettre`, { derogation: { motif } })); notifier('Révision émise avec dérogation et figée.'); }} />
      <DialogueRevuePrix ouvert={dialogue === 'revue'} fermer={() => setDialogue(null)} v={v} modifiable={modifiable} actualiser={actualiserPrix} />
      <DialogueVariante ouvert={dialogue === 'variante'} fermer={() => setDialogue(null)} creer={(nom) => nouvelleRevision(nom)} />
      <DialogueAcceptation ouvert={dialogue === 'acceptation'} fermer={() => setDialogue(null)} dossierId={dossierId} emiseLe={r.emitted_at}
        accepter={async (corps) => { await ecrire(() => api.post(`/api/devis/revisions/${rid}/acceptation`, corps)); notifier('Acceptation du client enregistrée.'); }} />
      <DialogueAnnulationAcceptation ouvert={dialogue === 'annulation'} fermer={() => setDialogue(null)}
        annuler={async (motif) => { await ecrire(() => api.post(`/api/devis/revisions/${rid}/acceptation/annuler`, { motif })); notifier('Acceptation annulée.'); }} />
      {confirmation}
    </div>
  );
}

// ─── Tableau éditable (ordinateur) ───
function TableauLignes({ v, lignes, modifiable, patch, editer, accorder }: { v: VueRevision; lignes: Ligne[]; modifiable: boolean;
  patch: (lid: string, corps: Record<string, unknown>) => Promise<unknown>; editer: (l: Ligne) => void; accorder?: (l: Ligne) => Promise<void> }) {
  const couts = v.droits.couts;
  const nbCol = couts ? 11 : 8;
  const taux = v.taux_horaires;
  return (
    <div className="tableau-conteneur">
      <table className={`tableau devis-tableau ${couts ? 'devis-tableau-couts' : ''}`}>
        <thead>
          <tr>
            <th scope="col" className="devis-col-designation">Désignation</th>
            <th scope="col" className="devis-col-ref">Référence</th>
            <th scope="col" className="num devis-col-q">Quantité</th>
            <th scope="col" className="devis-col-u">Unité</th>
            {couts && <th scope="col" className="num devis-col-cout">Coût unitaire</th>}
            <th scope="col" className="num devis-col-h">Heures</th>
            {couts && <th scope="col" className="devis-col-taux">Taux horaire</th>}
            {couts && <th scope="col" className="num devis-col-pct">{v.revision.pricing_mode === 'marge' ? 'Marge %' : 'Majoration %'}</th>}
            <th scope="col" className="num">Prix unitaire HT</th>
            <th scope="col" className="num">Total HT</th>
            <th scope="col"><span className="visuellement-cache">Actions</span></th>
          </tr>
        </thead>
        {SECTIONS.map((s) => {
          const ls = lignes.filter((l) => l.section === s);
          if (!ls.length) return null;
          return (
            <tbody key={s}>
              <tr className="devis-section"><th scope="colgroup" colSpan={nbCol - 2}>{LIBELLES_SECTIONS[s as Section]}</th>
                <td className="num" colSpan={2}><SousTotal lignes={ls} /></td></tr>
              {ls.map((l) => (
                <tr key={l.id} id={`ligne-${l.id}`} className={l.calcul.complet ? undefined : 'devis-ligne-incomplete'}>
                  <td>{modifiable
                    ? <SaisieAuto aria-label="Désignation" title={l.label} valeur={l.label} enregistrer={(x) => patch(l.id, { label: x })} />
                    : <span>{l.label}</span>}
                    {l.gamme_produit_id && <span className="devis-sous"><Badge ton="accent">Ma gamme</Badge></span>}
                    <EtatPrixGamme l={l} accorder={accorder} />
                    {l.requirement_label && <span className="tres-petit discret devis-sous">Exigence : {l.requirement_label}</span>}
                    {l.quantity_source && <span className="tres-petit discret devis-source-q">{l.quantity_source}</span>}
                  </td>
                  <td>{modifiable
                    ? <SaisieAuto aria-label="Référence" className="saisie-mono" valeur={l.reference ?? ''} enregistrer={(x) => patch(l.id, { reference: x || null })} />
                    : <span className="mono">{l.reference ?? ''}</span>}</td>
                  <td className="num">{modifiable
                    ? <SaisieAuto aria-label={l.net_quantity != null ? 'Besoin net' : 'Quantité'} montantFr className="devis-saisie-num" valeur={versSaisie(l.net_quantity ?? l.quantity)} enregistrer={(x) => patch(l.id, { quantity: x })} />
                    : quantite(l.quantity)}
                    {l.net_quantity != null && <span className="tres-petit discret devis-sous">{modifiable ? `net ; vendu ${quantite(l.quantity)}` : `net ${quantite(l.net_quantity)}`}, dont surplus {quantite(l.surplus_quantity ?? '0')}</span>}</td>
                  <td>{modifiable
                    ? <SaisieAuto aria-label="Unité" className="devis-saisie-u" valeur={l.unit} enregistrer={(x) => patch(l.id, { unit: x || 'u' })} />
                    : l.unit}</td>
                  {couts && (
                    <td className="num">
                      {l.achat_reserve ? <span className="tres-petit discret devis-reserve"><Lock size={12} aria-hidden /> {ACHAT_RESERVE}</span> : <>
                      {modifiable
                        ? <SaisieAuto aria-label="Coût unitaire" montantFr placeholder="À renseigner" className="devis-saisie-num" valeur={versSaisie(l.unit_cost)}
                            enregistrer={(x) => patch(l.id, { unitCost: x.trim() === '' ? null : x })} />
                        : <Montant v={l.unit_cost} />}
                      {l.unit_cost === null || l.unit_cost === undefined
                        ? modifiable && <span className="devis-sous"><BadgeARenseigner /></span>
                        : <span className="tres-petit discret devis-sous">{sourcePrix(l)}</span>}
                      {l.unit_cost_affiche && <span className="tres-petit devis-sous devis-reel">{`affiché ${euros(l.unit_cost_affiche)}`}</span>}
                      {l.calcul.frais_approche && <span className="tres-petit discret devis-sous">{`dont frais d’approche ${pct(l.calcul.frais_approche.pct)}${l.calcul.frais_approche.montant ? ` : ${euros(l.calcul.frais_approche.montant)}` : ''}`}</span>}
                      {v.alertes_prix && <BadgesAlertePrix a={v.alertes_prix.lignes[l.id]} seuil={v.alertes_prix.seuil_mois} />}
                      </>}
                    </td>
                  )}
                  <td className="num">{modifiable
                    ? <SaisieAuto aria-label="Heures unitaires" montantFr className="devis-saisie-num" valeur={versSaisie(l.labor_hours)}
                        placeholder={l.labor_hours === null ? 'À renseigner' : undefined}
                        enregistrer={(x) => patch(l.id, { laborHours: x.trim() === '' ? '0' : x })} />
                    : l.labor_hours === null ? <span className="tres-petit devis-motif">À renseigner</span> : quantite(l.labor_hours)}</td>
                  {couts && (
                    <td>{modifiable
                      ? <select className="saisie" aria-label="Taux horaire" value={l.labor_rate_code ?? ''}
                          onChange={(e) => { void patch(l.id, { laborRateCode: e.target.value || null }).catch(() => {}); }}>
                          <option value="">Aucun</option>
                          {taux.filter((x) => x.active || x.code === l.labor_rate_code).map((x) => <option key={x.code} value={x.code}>{x.code}</option>)}
                        </select>
                      : <span className="mono">{l.labor_rate_code ?? ''}</span>}
                      {l.calcul.cout_horaire && <span className="tres-petit discret devis-sous">{euros(l.calcul.cout_horaire)} / h</span>}
                    </td>
                  )}
                  {couts && (
                    <td className="num">{l.achat_reserve ? <span className="tres-petit discret devis-reserve"><Lock size={12} aria-hidden /> {TAUX_RESERVE}</span> : modifiable
                      ? <SaisieAuto aria-label="Taux propre à la ligne" montantFr className="devis-saisie-num" placeholder={versSaisie(v.revision.default_rate)}
                          valeur={versSaisie(l.rate_override)} enregistrer={(x) => patch(l.id, { rateOverride: x.trim() === '' ? null : x })} />
                      : pct(l.calcul.taux_applique)}
                      {l.calcul.marge_pct && <span className="tres-petit discret devis-sous">marge {pct(l.calcul.marge_pct)}</span>}
                      {l.calcul.reel?.marge_pct && <span className="tres-petit devis-sous devis-reel">réelle {pct(l.calcul.reel.marge_pct)}</span>}
                    </td>
                  )}
                  <td className="num">{l.prix_reserve ? <span className="tres-petit discret devis-reserve"><Lock size={12} aria-hidden /> {PRIX_RESERVE}</span>
                    : l.calcul.prix_unitaire_ht === null ? <span className="tres-petit devis-motif">À renseigner</span> : <Montant v={l.calcul.prix_unitaire_ht} />}
                    {l.calcul.prix_convenu && <span className="tres-petit discret devis-sous" title="Prix du document signé ou commandé : il ne dépend pas du coût">Prix convenu</span>}</td>
                  <td className="num">{l.prix_reserve ? <span className="tres-petit discret devis-reserve"><Lock size={12} aria-hidden /> Réservé</span> : <Montant v={l.calcul.total_ht} fort />}
                    {!l.calcul.complet && <span className="tres-petit devis-motif devis-sous">{l.calcul.a_renseigner.map((m) => LIBELLES_MOTIFS[m] ?? m).join(' ; ')}</span>}
                  </td>
                  <td>{modifiable && <Bouton compact variante="fantome" className="btn-icone" aria-label={`Modifier « ${l.label} »`} title="Modifier, déplacer, supprimer"
                    onClick={() => editer(l)}><Pencil aria-hidden /></Bouton>}</td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

function SousTotal({ lignes }: { lignes: Ligne[] }) {
  // Texte plus long que les 2 colonnes chiffrées : il revient à la ligne au lieu d'être coupé (cellule .num en nowrap).
  if (lignes.some((l) => l.prix_reserve)) return <span className="tres-petit discret" style={{ whiteSpace: 'normal' }}>Sous-total réservé (Ma gamme)</span>;
  if (lignes.some((l) => l.calcul.total_ht === null)) return <span className="tres-petit discret">Sous-total incomplet</span>;
  const centimes = lignes.reduce((s, l) => s + BigInt(l.calcul.total_ht!.replace('.', '')), 0n); // somme exacte en centimes
  const txt = `${centimes / 100n}.${String(centimes % 100n).padStart(2, '0')}`;
  return <span className="chiffre petit secondaire">{euros(txt)}</span>;
}

// ─── Ma gamme : prix imposé aux comptes sans accès achats (décision du 01/11/2026) ───
/** Tarif du produit, « À fixer par la direction », « Sous le tarif — accord de la direction » et accord donné (qui, quand, prix). */
function EtatPrixGamme({ l, accorder }: { l: Ligne; accorder?: (l: Ligne) => Promise<void> }) {
  const [envoi, setEnvoi] = useState(false);
  const e = l.prix_gamme;
  if (!e) return null;
  // Accord possible sur une baisse sous le tarif, et sur une ligne « à fixer » dont le prix a été saisi par un compte sans accès achats.
  const attendAccord = !e.accord && (e.sous_tarif || (e.a_fixer && l.sale_unit_price != null && !!e.saisi_par));
  return (
    <span className="devis-sous devis-prix-gamme">
      {e.reconnue && <span className="tres-petit discret">Référence d’un produit Ma gamme</span>}
      {e.a_fixer && <Badge ton="ambre">{LIBELLE_A_FIXER}</Badge>}
      {e.a_fixer && l.sale_unit_price != null && e.saisi_par && <span className="tres-petit discret">Prix saisi par {e.saisi_par}</span>}
      {!e.a_fixer && e.tarif !== null && <span className="tres-petit discret">Tarif {euros(e.tarif)}</span>}
      {e.sous_tarif && !e.accord && <Badge ton="ambre">{LIBELLE_SOUS_TARIF}</Badge>}
      {e.accord && <span className="tres-petit discret">Accord de la direction{e.accord.par ? ` — ${e.accord.par}` : ''}, {dateHeure(e.accord.le)} ({euros(e.accord.prix)}{e.accord.quantite ? ` pour ${versSaisie(e.accord.quantite)} ${l.unit ?? ''}`.trimEnd() : ''})</span>}
      {attendAccord && accorder && (
        <Bouton compact variante="secondaire" icone={<BadgeCheck aria-hidden />} disabled={envoi}
          onClick={async () => { setEnvoi(true); try { await accorder(l); } catch { /* message déjà affiché */ } finally { setEnvoi(false); } }}>
          Accord de la direction</Bouton>
      )}
    </span>
  );
}

// ─── Cartes (mobile) ───
function CartesLignes({ v, lignes, modifiable, editer, accorder }: { v: VueRevision; lignes: Ligne[]; modifiable: boolean; editer: (l: Ligne) => void;
  accorder?: (l: Ligne) => Promise<void> }) {
  const couts = v.droits.couts;
  return (
    <div className="devis-cartes">
      {SECTIONS.map((s) => {
        const ls = lignes.filter((l) => l.section === s);
        if (!ls.length) return null;
        return (
          <section key={s} className="devis-cartes-section" aria-label={LIBELLES_SECTIONS[s]}>
            <h3 className="devis-cartes-titre"><span>{LIBELLES_SECTIONS[s]}</span><SousTotal lignes={ls} /></h3>
            {ls.map((l) => (
              <article key={l.id} className={`devis-carte ${l.calcul.complet ? '' : 'devis-ligne-incomplete'}`}>
                <div className="devis-carte-haut">
                  <strong className="devis-carte-titre">{l.label}{l.gamme_produit_id && <> <Badge ton="accent">Ma gamme</Badge></>}</strong>
                  {l.prix_reserve ? <span className="tres-petit discret devis-reserve"><Lock size={12} aria-hidden /> {PRIX_RESERVE}</span> : <Montant v={l.calcul.total_ht} fort />}
                </div>
                {(l.reference || l.manufacturer) && <span className="petit"><span className="mono">{l.reference}</span>{l.manufacturer ? ` — ${l.manufacturer}` : ''}</span>}
                <EtatPrixGamme l={l} accorder={accorder} />
                <span className="petit secondaire chiffre">
                  {quantite(l.quantity)} {l.unit}{l.net_quantity != null ? ` (dont surplus ${quantite(l.surplus_quantity ?? '0')})` : ''} × {l.prix_reserve ? 'prix réservé à la direction' : l.calcul.prix_unitaire_ht === null ? 'prix à renseigner' : euros(l.calcul.prix_unitaire_ht)}
                  {l.labor_hours === null ? ' — temps de pose à renseigner' : Number(l.labor_hours) > 0 && ` — ${quantite(l.labor_hours)} h par unité`}
                </span>
                {couts && (
                  <span className="tres-petit discret">
                    {l.achat_reserve ? ACHAT_RESERVE : l.unit_cost === null || l.unit_cost === undefined ? 'Coût unitaire à renseigner' : `Coût ${euros(l.unit_cost)}${sourcePrix(l) ? ` — ${sourcePrix(l)}` : ''}`}
                    {l.labor_rate_code ? ` — taux ${l.labor_rate_code}` : ''}{l.calcul.marge_pct ? ` — marge ${pct(l.calcul.marge_pct)}` : ''}
                    {l.calcul.frais_approche ? ` — dont frais d’approche ${pct(l.calcul.frais_approche.pct)}` : ''}
                    {l.unit_cost_affiche ? ` — affiché ${euros(l.unit_cost_affiche)}` : ''}{l.calcul.reel?.marge_pct ? ` — marge réelle ${pct(l.calcul.reel.marge_pct)}` : ''}
                  </span>
                )}
                {!l.calcul.complet && <span className="tres-petit devis-motif">{l.calcul.a_renseigner.map((m) => LIBELLES_MOTIFS[m] ?? m).join(' ; ')}</span>}
                {l.calcul.prix_convenu && <span className="tres-petit discret">Prix convenu (document signé ou commandé){couts && l.calcul.cout_a_renseigner?.length ? ' — coût à renseigner, marge inconnue' : ''}</span>}
                {v.alertes_prix && <BadgesAlertePrix a={v.alertes_prix.lignes[l.id]} seuil={v.alertes_prix.seuil_mois} />}
                {l.requirement_label && <span className="tres-petit discret">Exigence : {l.requirement_label}</span>}
                {l.quantity_source && <span className="tres-petit discret devis-source-q">{l.quantity_source}</span>}
                {modifiable && <Bouton compact icone={<Pencil aria-hidden />} onClick={() => editer(l)}>Modifier</Bouton>}
              </article>
            ))}
          </section>
        );
      })}
    </div>
  );
}

// ─── Totaux ───
/** Prix d'achat d'un produit Ma gamme : réservé à l'administrateur et à l'accès achats (décision du 31/10/2026). */
const ACHAT_RESERVE = 'Prix d’achat réservé (Ma gamme)';
/** Taux d'une ligne Ma gamme pour un compte sans accès achats (décision du 01/11/2026) : il redonnerait le prix d'achat. */
const TAUX_RESERVE = 'Taux réservé';
const TOTAL_RESERVE = 'Réservé : le devis contient des produits Ma gamme (accès achats)';
/** Coût ou marge inconnu : une ligne à prix convenu n'a pas encore de coût (jamais assimilé à zéro). */
const INCONNU_CONVENU = 'Inconnu : coût à renseigner sur une ligne à prix convenu';
function LigneTotal({ libelle, valeur, fort }: { libelle: ReactNode; valeur: ReactNode; fort?: boolean }) {
  return <div className={`devis-total ${fort ? 'devis-total-fort' : ''}`}><dt>{libelle}</dt><dd>{valeur}</dd></div>;
}

function BlocTotaux({ b, remisePct, tva, couts, suffixe, surGamme }: { b: Bloc; remisePct: string; tva: string; couts: boolean; suffixe: string; surGamme?: boolean }) {
  return (
    <dl className="devis-totaux">
      {couts && b.achats_reserves && <LigneTotal libelle={`Coût de revient et marge${suffixe}`} valeur={<span className="tres-petit discret"><Lock size={12} aria-hidden /> {TOTAL_RESERVE}</span>} />}
      {couts && !b.achats_reserves && b.cout_revient !== undefined && <LigneTotal libelle={`Coût de revient${suffixe}`} valeur={b.cout_revient === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span> : <Montant v={b.cout_revient} />} />}
      {Number(b.remise) > 0 && <LigneTotal libelle={`Total HT avant remise${suffixe}`} valeur={<Montant v={b.ht_brut} />} />}
      {Number(b.remise) > 0 && <LigneTotal libelle={surGamme ? `Remise ${pct(remisePct)} sur les lignes Ma gamme (${euros(b.remise_base ?? '0')} HT)` : `Remise ${pct(remisePct)}`} valeur={<span className="chiffre">−{euros(b.remise)}</span>} />}
      <LigneTotal libelle={`Total HT${suffixe}`} valeur={<Montant v={b.ht_net} fort />} fort />
      <LigneTotal libelle={`TVA ${pct(tva)}`} valeur={<Montant v={b.tva} />} />
      <LigneTotal libelle={`Total TTC${suffixe}`} valeur={<Montant v={b.ttc} fort />} fort />
      {couts && b.achats_reserves && b.hors_gamme && (
        <>
          {/* Total partiel : l'agrégat hors Ma gamme ne compte, lui aussi, que les lignes complètes (suffixe « hors lignes à renseigner »). */}
          <LigneTotal libelle={`Coût de revient ${b.hors_gamme.libelle}${suffixe}`} valeur={b.hors_gamme.cout_revient === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span> : <Montant v={b.hors_gamme.cout_revient} />} />
          <LigneTotal libelle={`Marge ${b.hors_gamme.libelle}${suffixe}`} valeur={b.hors_gamme.marge === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span>
            : <span className="chiffre"><Montant v={b.hors_gamme.marge} />{b.hors_gamme.marge_pct !== null && <span className="tres-petit discret"> ({pct(b.hors_gamme.marge_pct)})</span>}</span>} />
        </>
      )}
      {couts && !b.achats_reserves && b.marge !== undefined && (
        <>
          <LigneTotal libelle={`Marge dégagée${suffixe}`} valeur={b.marge === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span> : <Montant v={b.marge} />} />
          <LigneTotal libelle="Marge réalisée, en % du prix de vente HT" valeur={<span className="chiffre">{pct(b.marge_pct)}</span>} />
          <LigneTotal libelle="Majoration équivalente, en % du coût de revient" valeur={<span className="chiffre">{pct(b.majoration_pct)}</span>} />
        </>
      )}
      {couts && b.reel && (
        <>
          <LigneTotal libelle={`Coût de revient réel${suffixe}`} valeur={b.reel.cout_revient === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span> : <Montant v={b.reel.cout_revient} />} />
          <LigneTotal libelle={`Marge réelle${suffixe}`} valeur={b.reel.marge === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span> : <Montant v={b.reel.marge} fort />} fort />
          <LigneTotal libelle="Marge réelle, en % du prix de vente HT" valeur={<span className="chiffre">{pct(b.reel.marge_pct)}</span>} />
        </>
      )}
    </dl>
  );
}

function PanneauTotaux({ v }: { v: VueRevision }) {
  const t = v.totaux;
  if (t.totaux_reserves) {
    return (
      <p className="petit discret devis-totaux-reserves"><Lock size={14} aria-hidden /> {TOTAUX_RESERVES} : le devis contient des produits Ma gamme
        dont les prix sont réservés à la direction, au chargé d’affaires et au référent. Ni total, ni remise, ni PDF client.</p>
    );
  }
  if (t.complet) return <BlocTotaux b={t.totaux!} remisePct={t.remise_pct ?? '0'} tva={t.taux_tva} couts={v.droits.couts} suffixe="" surGamme={t.remise_assiette === 'ma_gamme'} />;
  return (
    <div className="pile">
      <Alerte ton="ambre" titre={`Total incomplet : ${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner`}>
        Les totaux du devis ne sont pas calculables. Montants ci-dessous : total partiel, hors lignes à renseigner.
      </Alerte>
      <BlocTotaux b={t.partiel!} remisePct={t.remise_pct ?? '0'} tva={t.taux_tva} couts={v.droits.couts} suffixe=" (hors lignes à renseigner)" surGamme={t.remise_assiette === 'ma_gamme'} />
    </div>
  );
}

// ─── Paramètres de calcul ───
function PanneauParametres({ v, modifiable, patch }: { v: VueRevision; modifiable: boolean; patch: (c: Record<string, unknown>) => Promise<unknown> }) {
  const r = v.revision;
  const couts = v.droits.couts;
  const champ = (libelle: string, valeur: string, cle: string, aide?: string, suffixe = '%') => (
    <div className="champ">
      <span className="champ-libelle" id={`lib-${cle}`}>{libelle}</span>
      {modifiable
        ? <SaisieAuto aria-labelledby={`lib-${cle}`} montantFr valeur={valeur} enregistrer={(x) => patch({ [cle]: cle === 'validityDays' ? Number(x) : x || '0' })} />
        : <span className="chiffre">{valeur} {suffixe}</span>}
      {aide && <span className="champ-aide">{aide}</span>}
    </div>
  );
  return (
    <div className="pile">
      {couts && (
        <div className="champ">
          <span className="champ-libelle" id="lib-mode">Mode de calcul du prix de vente</span>
          <div className="segment devis-segment" role="group" aria-labelledby="lib-mode">
            <button type="button" aria-pressed={r.pricing_mode === 'marge'} disabled={!modifiable}
              onClick={() => { void patch({ pricingMode: 'marge' }).catch(() => {}); }}>Marge sur prix de vente</button>
            <button type="button" aria-pressed={r.pricing_mode === 'majoration'} disabled={!modifiable}
              onClick={() => { void patch({ pricingMode: 'majoration' }).catch(() => {}); }}>Majoration du coût</button>
          </div>
          <span className="champ-aide">{r.pricing_mode === 'marge'
            ? 'Prix = coût ÷ (1 − taux) : le taux est la part de marge dans le prix de vente (25 % de marge ⇔ 33,33 % de majoration).'
            : 'Prix = coût × (1 + taux) : le taux s’applique au coût de revient (25 % de majoration ⇔ 20 % de marge).'}</span>
        </div>
      )}
      <div className="grille-2">
        {couts && champ(r.pricing_mode === 'marge' ? 'Taux de marge par défaut (%)' : 'Taux de majoration par défaut (%)', versSaisie(r.default_rate), 'defaultRate',
          'Appliqué aux lignes sans taux propre')}
        {r.discount_pct === undefined
          ? <div className="champ"><span className="champ-libelle">Remise globale (%)</span>
            <span className="petit discret devis-totaux-reserves"><Lock size={12} aria-hidden /> {TOTAUX_RESERVES}</span></div>
          : champ('Remise globale (%)', versSaisie(r.discount_pct), 'discountPct',
            r.discount_scope === 'ma_gamme' ? 'Porte sur les seules lignes Ma gamme (remise proposée, choisie par le dirigeant). À 0, elle repasse sur tout le devis.' : undefined)}
        {champ('TVA (%)', versSaisie(r.vat_rate), 'vatRate', 'Taux normal 20 % ; taux réduits 10 % ou 5,5 % selon les conditions applicables')}
        {champ('Validité de l’offre (jours)', String(r.validity_days), 'validityDays', undefined, 'jours')}
      </div>
      {!couts && <p className="petit discret"><Lock size={14} aria-hidden style={{ display: 'inline', verticalAlign: '-2px' }} /> Coûts, taux et marges réservés aux rôles habilités.</p>}
    </div>
  );
}

function TexteAuto({ libelle, valeur, modifiable, enregistrer, vide, aide }: { libelle: string; valeur: string; modifiable: boolean;
  enregistrer: (v: string) => Promise<unknown>; vide: string; aide: string }) {
  const [local, setLocal] = useState(valeur);
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setLocal(valeur); }, [valeur, focus]);
  if (!modifiable) {
    return <div className="champ"><span className="champ-libelle">{libelle}</span>
      {valeur ? <p className="devis-texte">{valeur}</p> : <span className="inconnu">{vide}</span>}</div>;
  }
  return (
    <div className="champ">
      <label className="champ-libelle" htmlFor={`txt-${libelle}`}>{libelle}</label>
      <textarea id={`txt-${libelle}`} className="saisie" rows={5} maxLength={8000} value={local}
        onFocus={() => setFocus(true)} onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setFocus(false); if (local !== valeur) void enregistrer(local).catch(() => {}); }} />
      <span className="champ-aide">{aide}</span>
    </div>
  );
}
